"use client";
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight } from 'lucide-react';
import "../../scss/_resources.scss";
import heroImage from '../../../public/icons/hero.png';
import energySectorImage from '../../../public/icons/energySector.png';
import roundarrow from '../../../public/icons/roundarrow.png';
import roundarrowwhite from '../../../public/icons/roundarrowwhite.png';
import { getFeedList } from '@/services/publicService';

const FILTER_OPTIONS = [
  { label: 'All', value: 'All' },
  { label: 'Articles', value: 'articles' },
  { label: 'News', value: 'news' },
  { label: 'Market Insight', value: 'market-insight' },
];

const TYPE_LABEL = {
  articles: 'Article',
  article: 'Article',
  news: 'News',
  'market-insight': 'Market Insight',
  market_insight: 'Market Insight',
};

const detailHrefFor = (item) => {
  const slug = item?.slug || item?.id || '';
  if (!slug) return '#';
  const type = (item?.type || item?.feed_type || '').toString().toLowerCase();
  if (type.includes('news')) return `/news/${slug}`;
  if (type.includes('market')) return `/market-insight/${slug}`;
  return `/article/${slug}`;
};

function getVisiblePages(current, last, width = 5) {
  if (last <= width) {
    return Array.from({ length: last }, (_, i) => i + 1);
  }

  const half = Math.floor(width / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(last, start + width - 1);

  if (end - start < width - 1) {
    start = Math.max(1, end - width + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export default function Resources() {
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [publications, setPublications] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(false);
  const [guideItem, setGuideItem] = useState(null);
  const [latestNewsItems, setLatestNewsItems] = useState([]);
  const [latestArticleItem, setLatestArticleItem] = useState(null);
  const [secondaryMarketInsightItem, setSecondaryMarketInsightItem] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const publicationsRef = useRef(null);

  const extractList = (resp) => {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp.data)) return resp.data;
    if (Array.isArray(resp?.data?.data)) return resp.data.data;
    if (Array.isArray(resp.items)) return resp.items;
    if (Array.isArray(resp.results)) return resp.results;
    return [];
  };

  const extractPagination = (resp) => {
    const p = resp?.pagination || resp?.meta || resp?.data?.pagination || {};
    return {
      current_page: Number(p.current_page || p.currentPage || 1),
      last_page: Number(p.last_page || p.lastPage || p.total_pages || 1),
    };
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { page: currentPage, per_page: 9 };
        if (selectedFilter !== 'All') params.type = selectedFilter;
        if (appliedSearch) params.search = appliedSearch;
        const resp = await getFeedList(params);
        if (cancelled) return;
        setPublications(
          extractList(resp).map((x) => ({
            ...x,
            _type: selectedFilter === 'All' ? (x?.type || x?.feed_type) : selectedFilter,
          }))
        );
        setPagination(extractPagination(resp));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [selectedFilter, currentPage, appliedSearch]);

  const handleSearch = () => {
    setAppliedSearch(searchInput.trim());
    setCurrentPage(1);
    setTimeout(() => {
      publicationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [marketInsightResp, newsResp, articleResp] = await Promise.all([
        getFeedList({ type: 'market-insight', per_page: 2 }),
        getFeedList({ type: 'news', per_page: 2 }),
        getFeedList({ type: 'articles', per_page: 1 }),
      ]);
      if (cancelled) return;
      const marketInsights = extractList(marketInsightResp);
      setGuideItem(marketInsights[0] || null);
      setSecondaryMarketInsightItem(marketInsights[1] || null);
      setLatestNewsItems(extractList(newsResp).slice(0, 2));
      setLatestArticleItem(extractList(articleResp)[0] || null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const featuredItems = [
    ...latestNewsItems,
    ...(latestArticleItem ? [latestArticleItem] : []),
    ...(secondaryMarketInsightItem ? [secondaryMarketInsightItem] : []),
  ];

  const marketInsightBanners = [
    {
      category: "Market Insights",
      title: "Learn how winter and summer fuel use influence short-term market moves.",
      link: "#"
    },
    {
      category: "Market Insights",
      title: "Learn how winter and summer fuel use influence short-term market moves.",
      link: "#"
    }
  ];

  const totalPages = pagination.last_page || 1;
  const visiblePages = getVisiblePages(currentPage, totalPages, 5);
  const currentData = publications;
  const guideImage = guideItem?.image?.url || guideItem?.image || guideItem?.feature_image || guideItem?.featured_image;

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  return (
    <div className="resources-page-res">
      {/* search Section */}
      <section className="hero-section-res">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="hero-content-res">
                <h1 className="hero-title-res">Everything You Need to Know Before You Invest in Oil & Gas</h1>
                <p className="hero-subtitle-res">
                  Explore our resource hub and gain access to our curated tools, guides, reports, and more for smarter energy investments.
                </p>
                <div className="search-bar-wrapper-res">
                  <input
                    type="text"
                    className="search-input-res"
                    placeholder="Search resources..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                  />
                  <button className="search-button-res" onClick={handleSearch}>Search</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Content Section */}
      <section className="featured-section-res">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 col-md-12">
              <div className="featured-image-wrapper-res">
                <Image
                  src={guideImage && typeof guideImage === 'string' && guideImage.trim() ? guideImage : heroImage}
                  alt={guideItem?.title || "Latest Market Insight"}
                  className="featured-image-res"
                  fill
                  style={{ objectFit: 'cover' }}
                  unoptimized={typeof guideImage === 'string'}
                />
              </div>

              {/* Guide to the Markets Section */}
              <div className="guide-section-res">
                <div className="guide-card-res">
                  <span className="guide-category-res">Market Insight</span>
                  <h2 className="guide-title-res">
                    {guideItem?.title || 'Guide to the markets'}
                  </h2>
                  <p className="guide-description-res">
                    {guideItem?.description || guideItem?.excerpt || guideItem?.short_description || 'Investin Energy Guide to the markets illustrates a comprehensive array of market and economic histories, trends and statistics through clear charts and graphs.'}
                  </p>
                  <Link
                    href={detailHrefFor(guideItem)}
                    className="read-more-link-res"
                  >
                    Read More
                    <Image src={roundarrow} alt='roundarrow' />
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-md-12">
              <div className="featured-articles-res">
                {featuredItems.map((article, index) => {
                  const title = article?.title || '';
                  const category = TYPE_LABEL[article?.type] || TYPE_LABEL[article?.feed_type] || 'Article';
                  const href = detailHrefFor(article);
                  return (
                    <div key={article?.id || article?.slug || index} className="featured-article-item-res">
                      <span className="article-category-res">{category}</span>
                      <h3 className="article-title-res">{title}</h3>
                      <Link href={href} className="read-more-link-res">
                        Read More
                        <Image src={roundarrow} alt='' />
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Market Insights Banners Section */}
      <section className="banners-section-res">
        <div className="container">
          <div className="row">
            {marketInsightBanners.map((banner, index) => (
              <div key={index} className="col-lg-6 col-md-12 mb-4 wrap-market">
                <div className="market-banner-res">
                  <span className="banner-category-res">{banner.category}</span>
                  <h3 className="banner-title-res">{banner.title}</h3>
                  <a href={banner.link} className="read-more-link-res">
                    Read More
                    {/* <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 12L12 4M12 4H6M12 4V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg> */}
                    <Image src={roundarrowwhite} alt='roundarrowwhite' />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Publications Section */}
      <section className="publications-section-res" ref={publicationsRef}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="publications-header-res">
                <h2 className="publications-title-res">
                  {appliedSearch ? `Search results for "${appliedSearch}"` : 'All Publications'}
                </h2>
                <div className="filter-wrapper-res">
                  <select
                    className="filter-dropdown-res"
                    value={selectedFilter}
                    onChange={(e) => handleFilterChange(e.target.value)}
                  >
                    {FILTER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className='row'>
          {loading ? (
            <div className="col-12 text-center py-5">
              <p>Loading...</p>
            </div>
          ) : currentData.length > 0 ? (
            currentData.map((publication, index) => {
              const imgSrc = publication?.image?.url || publication?.image || publication?.feature_image || publication?.featured_image;
              const title = publication?.title || '';
              const description = publication?.description || publication?.excerpt || publication?.short_description || '';
              const typeKey = publication?._type || publication?.type || '';
              const categoryLabel = TYPE_LABEL[typeKey] || publication?.category?.name || 'Article';
              const href = detailHrefFor({ ...publication, type: typeKey });
              return (
                <div key={`${typeKey}-${publication?.id || publication?.slug || index}`} className="col-lg-4 col-md-6 col-sm-12 mb-4">
                  <div className="publication-card-res">
                    <div className="publication-image-wrapper-res">
                      <Image
                        src={imgSrc && typeof imgSrc === 'string' && imgSrc.trim() ? imgSrc : energySectorImage}
                        alt={title || 'publication'}
                        className="publication-image-res"
                        fill
                        style={{ objectFit: 'cover' }}
                        unoptimized={typeof imgSrc === 'string'}
                      />
                    </div>
                    <div className="publication-content-res">
                      <div><span className="publication-category-res">{categoryLabel}</span></div>
                      <h3 className="publication-card-title-res">{title}</h3>
                      <p className="publication-description-res">{description}</p>
                      <Link href={href} className="read-more-link-res">
                        Read More
                        <Image src={roundarrow} alt='roundarrow' />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-12 text-center py-5">
              <p>No publications found.</p>
            </div>
          )}
          </div>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="row">
              <div className="col-12">
                <div className="pagination-container">
                  <button
                    type="button"
                    className="pag-icon-btn"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage <= 1}
                    aria-label="First page"
                  >
                    <ChevronsLeft size={20} strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    className="pag-box-btn pag-box-btn--prev"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  {visiblePages.map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`pag-num ${currentPage === page ? 'is-active' : ''}`}
                      onClick={() => handlePageChange(page)}
                      disabled={currentPage === page}
                      aria-label={`Page ${page}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="pag-box-btn pag-box-btn--next"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <button
                    type="button"
                    className="pag-icon-btn pag-icon-btn--emphasis"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage >= totalPages}
                    aria-label="Last page"
                  >
                    <ChevronsRight size={20} strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

